import { useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { startLoadingTask, completeLoadingTask, resetLoadingState, selectIsTaskLoading, selectIsTaskCompleted } from "../redux/Slices/loadingSlice";

interface UseMultipleApiLoadingProps {
  taskIds: string[];
  onAllCompleted?: () => void;
  resetOnMount?: boolean;
}

export const useMultipleApiLoading = ({ 
  taskIds, 
  onAllCompleted, 
  resetOnMount = true 
}: UseMultipleApiLoadingProps) => {
  const dispatch = useAppDispatch();
  const isAnyLoading = useAppSelector((state) => state.loader.isLoading);
  const completedTasks = useAppSelector((state) => Array.from(state.loader.completedTasks));
  const loadingTasks = useAppSelector((state) => Array.from(state.loader.loadingTasks));
  const allTasksCompleted = useRef(false);

  // Reset loading state on mount if requested
  useEffect(() => {
    if (resetOnMount) {
      dispatch(resetLoadingState());
    }
  }, [dispatch, resetOnMount]);

  // Check if all tasks are completed
  useEffect(() => {
    const allCompleted = taskIds.every(taskId => 
      completedTasks.includes(taskId) || !loadingTasks.includes(taskId)
    );
    
    if (allCompleted && !allTasksCompleted.current && taskIds.length > 0) {
      allTasksCompleted.current = true;
      onAllCompleted?.();
    }
  }, [completedTasks, loadingTasks, taskIds, onAllCompleted]);

  // Helper functions - memoized to prevent infinite re-renders
  const startTask = useCallback((taskId: string) => {
    dispatch(startLoadingTask(taskId));
  }, [dispatch]);

  const completeTask = useCallback((taskId: string) => {
    dispatch(completeLoadingTask(taskId));
  }, [dispatch]);

  const isTaskLoading = (taskId: string) => {
    return useAppSelector(selectIsTaskLoading(taskId));
  };

  const isTaskCompleted = (taskId: string) => {
    return useAppSelector(selectIsTaskCompleted(taskId));
  };

  const reset = useCallback(() => {
    dispatch(resetLoadingState());
    allTasksCompleted.current = false;
  }, [dispatch]);

  return {
    isAnyLoading,
    isAllCompleted: allTasksCompleted.current,
    completedTasks,
    loadingTasks,
    startTask,
    completeTask,
    isTaskLoading,
    isTaskCompleted,
    reset
  };
};
